/**
 * List — DGUI component for ordered or unordered list of items.
 * Presentational only; schema-driven from agent/backend.
 */

import {
  Card,
  CardContent,
  List as MuiList,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import type { ListDGUIProps } from '@flowrithm/shared/src/features/ai-assist/types/dgui';

const cardSx = {
  borderRadius: 2,
  borderColor: 'rgba(4, 55, 123, 0.2)',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  maxWidth: 360,
  mt: 1,
  mb: 1,
};

const variantSpacing = {
  default: { py: 1.5, px: 2 },
  compact: { py: 1, px: 1.5 },
} as const;

function isListItemObject(
  item: string | { primary: string; secondary?: string }
): item is { primary: string; secondary?: string } {
  return typeof item === 'object' && item !== null && 'primary' in item;
}

export function List({
  title,
  subtitle,
  items,
  ordered = false,
  variant = 'default',
}: ListDGUIProps) {
  const spacing = variantSpacing[variant ?? 'default'] ?? variantSpacing.default;
  const safeItems = Array.isArray(items) ? items : [];

  return (
    <Card variant="outlined" sx={cardSx}>
      <CardContent sx={{ ...spacing, '&:last-child': { pb: spacing.py } }}>
        {title && (
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color="#04377b"
            gutterBottom
          >
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        <MuiList
          component={ordered ? 'ol' : 'ul'}
          disablePadding
          sx={{
            listStyle: ordered ? 'decimal' : 'disc',
            pl: ordered ? 2.5 : 2,
            '& .MuiListItem-root': {
              display: 'list-item',
              py: variant === 'compact' ? 0.25 : 0.5,
            },
          }}
        >
          {safeItems.map((item, i) =>
            isListItemObject(item) ? (
              <ListItem key={i} disablePadding>
                <ListItemText
                  primary={item.primary}
                  secondary={item.secondary}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
            ) : (
              <ListItem key={i} disablePadding>
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            )
          )}
        </MuiList>
      </CardContent>
    </Card>
  );
}
